import { action, computed, observable } from 'mobx';
import sumBy from 'lodash/sumBy';
import { CXBaseBlock } from '~/components/ClientExperience/OneFlow/models/cx-base-block.model';
import { DiscountTypes, IInvoiceSummary, TaxType } from '~/types/model.types';
import type { CXInvoiceBlock } from '~/components/ClientExperience/OneFlow/models/cx-invoice-block.model';
import type { CXServiceItemBlock } from '~/components/ClientExperience/OneFlow/models/cx-service-item-block.model';
import {
    IInvoiceSummaryBlockResponse,
    IInvoiceSummaryVariableValue
} from '~/types/server.types';
import { Variable } from '~/models/variable.model';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { FlowRevision } from '~/models/flow-revision.model';
import { taxAmountWithDiscounts } from '~/components/Payments/utils';
import { isPublicTemplateViewMode } from '~/utils/public-template-view-mode-flow.utils';

/**
 * NOTE: do not use connectedVariable to return data from the BE, since it will not work for preview mode when
 * there is temporary data such as selected services.
 */
export class CXInvoiceSummaryBlock extends CXBaseBlock {
    type: 'invoice_summary_block';
    @observable invoice_summary: IInvoiceSummary;

    constructor(
        block: IInvoiceSummaryBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    @computed
    get totalLateFees() {
        return sumBy(this.paymentScheduleBlock?.paymentBlocks, 'lateFee');
    }

    @computed
    get paymentScheduleBlock() {
        return (this.parent as CXInvoiceBlock).invoicePaymentScheduleBlock;
    }

    @computed
    get isLateFeeEnabled() {
        return !!this.invoice_summary.late_fee_enabled;
    }

    @action
    setIsLateFeeEnabled(enabled: boolean) {
        this.invoice_summary.late_fee_enabled = enabled;
    }

    @computed
    get lateFeePercentage() {
        return this.invoice_summary.late_fee_percentage;
    }

    @action
    setLateFeePercentage(percentage: number) {
        this.invoice_summary.late_fee_percentage = percentage;
    }

    hasTax(taxNumber) {
        return !!this.invoice_summary?.[taxNumber]?.active;
    }

    @computed
    get hasDiscount() {
        return !!this.invoice_summary.discount_type;
    }

    taxAmount(taxNumber: string): number {
        return this.calculatedTaxAmount(taxNumber);
    }

    calculatedTaxAmount(
        taxNumber: string,
        withPayInFullDiscount = this.isPayInFullDiscountApplied
    ): number {
        if (!this.invoice_summary[taxNumber]?.active) {
            return 0;
        }
        if (this.invoice_summary[taxNumber]?.type === TaxType.Absolute) {
            return this.invoice_summary[taxNumber]?.value;
        }
        const servicesWithTax = this.getServicesWithTax(taxNumber);

        return taxAmountWithDiscounts(
            this.invoice_summary,
            this.subTotal,
            servicesWithTax,
            taxNumber,
            withPayInFullDiscount
        );
    }

    @computed
    get subTotal() {
        return sumBy(
            (this.parent as CXInvoiceBlock).allMainServiceItemBlocks,
            (serviceItem: CXServiceItemBlock) => {
                let { totalPrice } = serviceItem;
                if (serviceItem.isPackage) {
                    totalPrice += sumBy(
                        serviceItem.childBlocks,
                        (packageServiceItem: CXServiceItemBlock) => {
                            return packageServiceItem.totalPrice;
                        }
                    );
                }
                return totalPrice;
            }
        );
    }

    @computed
    get discountAmount() {
        if (this.isRelativeDiscount()) {
            return this.subTotal * (this.invoice_summary.discount / 100);
        }
        return this.invoice_summary.discount;
    }

    @computed
    get grandTotal() {
        if (
            this.flow.isLeadForm ||
            isPublicTemplateViewMode() ||
            this.connectedVariable?.value?.total_price == null
        ) {
            return this.calculatedGrandTotal;
        }

        return this.connectedVariable.value.total_price;
    }

    @computed
    private get calculatedGrandTotal() {
        let total =
            this.subTotal -
            this.discountAmount +
            this.calculatedTaxAmount('tax_1') +
            this.calculatedTaxAmount('tax_2') +
            this.calculatedTaxAmount('tax_3');
        if (this.isPayInFullDiscountApplied) {
            total -= this.calculatedPayInFullDiscount;
        }
        return total;
    }

    @computed
    get calculatedGrandTotalBeforePayInFullDiscount() {
        return (
            this.subTotal -
            this.discountAmount +
            this.calculatedTaxAmount('tax_1', false) +
            this.calculatedTaxAmount('tax_2', false) +
            this.calculatedTaxAmount('tax_3', false)
        );
    }

    @computed
    get grandTotalWithFees() {
        return this.grandTotal + this.totalLateFees;
    }

    @computed
    get calculatedGrandTotalWithFees() {
        return this.calculatedGrandTotal + this.totalLateFees;
    }

    @computed
    get calculatedPayInFullDiscount() {
        if (typeof this.payInFullDiscount !== 'number') {
            return 0;
        }
        if (this.payInFullDiscountType === DiscountTypes.Absolute) {
            return this.payInFullDiscount;
        }

        return (
            (this.subTotal - this.discountAmount) *
            (this.payInFullDiscount / 100)
        );
    }

    @computed
    get totalTax() {
        return (
            this.taxAmount('tax_1') +
            this.taxAmount('tax_2') +
            this.taxAmount('tax_3')
        );
    }

    @computed
    get remainingBalance() {
        return this.flow.revision.remainingBalance;
    }

    get connectedVariable(): Variable<IInvoiceSummaryVariableValue> {
        return super.connectedVariable;
    }

    @computed
    get invoiceBlock() {
        return this.parent as CXInvoiceBlock;
    }

    @computed
    get isClientPaymentPlan() {
        return (
            !this.invoiceBlock.isRecurringInvoice &&
            this.invoice_summary.is_client_payment_plan
        );
    }

    @computed
    get maxNumberOfPayments() {
        return this.invoice_summary.max_number_of_payments;
    }

    @computed
    get firstPaymentDueDateType() {
        return this.invoice_summary.first_payment_due_date_type;
    }

    @computed
    get firstPaymentDueDate() {
        return this.invoice_summary.first_payment_due_date;
    }

    @computed
    get numberOfPayments() {
        return this.connectedVariable.value.number_of_payments;
    }
    set numberOfPayments(value: number) {
        this.connectedVariable.value.number_of_payments = value;
    }

    @computed
    get isClientSelectedPaymentPlan() {
        return this.isClientPaymentPlan && this.numberOfPayments;
    }

    @computed
    get isPendingClientPaymentSelection() {
        return this.isClientPaymentPlan && !this.numberOfPayments;
    }

    @computed
    get isClientSelectedSplitPaymentPlan() {
        return this.isClientPaymentPlan && this.numberOfPayments > 1;
    }

    @computed
    get isClientSelectedPayInFullPlan() {
        return this.isClientPaymentPlan && this.numberOfPayments === 1;
    }

    @computed
    get clientPaymentSelectionOptions(): number[] {
        const options: number[] = [];
        for (let i = 2; i <= this.maxNumberOfPayments; i++) {
            options.push(i);
        }

        return options;
    }

    @computed
    get allowPayInFullDiscount() {
        return this.invoice_summary.allow_pay_in_full_discount;
    }

    @computed
    get isPayInFullDiscountApplied() {
        return (
            this.allowPayInFullDiscount && this.isClientSelectedPayInFullPlan
        );
    }

    @computed
    get payInFullDiscountType() {
        return this.invoice_summary.pay_in_full_discount_type;
    }

    @computed
    get payInFullDiscount() {
        return this.invoice_summary.pay_in_full_discount;
    }

    isRelativeDiscount() {
        return this.invoice_summary.discount_type === DiscountTypes.Relative;
    }

    getServicesWithTax(taxNumber: string) {
        const mainPackageItems = (this.parent as CXInvoiceBlock)
            .allMainServiceItemBlocks as CXServiceItemBlock[];
        return [
            ...mainPackageItems,
            ...mainPackageItems.flatMap(packageItem => packageItem.childBlocks)
        ].filter((serviceItemBlock: CXServiceItemBlock) => {
            return serviceItemBlock.service_item[taxNumber];
        }, []);
    }
}
